// ===================================================================
// KAIXO - GroupCallManager
// ===================================================================
// Componente top-level del módulo de llamadas grupales. Aislado por
// completo de Calls/CallManager.js (1-a-1) — no comparten estado ni
// imports salvo livekit-client. Se monta una sola vez en App.js (o
// en cualquier sitio top-level) y escucha:
//
//   - Socket: 'incoming_group_call' → muestra UI de entrante
//   - Socket: 'group_call_accepted' → otro usuario aceptó (info)
//   - Socket: 'group_call_user_left' → un participante salió
//   - Socket: 'group_call_ended' → todos colgaron
//   - Window: 'kaixo:group_call_start' → el chat lanza una llamada
//
// Para iniciar desde otra parte de la app (ej. botón del header del
// chat de grupo) basta con:
//
//   window.dispatchEvent(new CustomEvent('kaixo:group_call_start', {
//       detail: { groupId, groupName, groupAvatar, type: 'voice' | 'video' }
//   }));
//
// El componente se encarga del resto.
// ===================================================================

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
    Room, RoomEvent, Track,
    createLocalAudioTrack, createLocalVideoTrack
} from 'livekit-client';
import { Icons } from '../Icons/Icons';
import { getSocket } from '../../services/socket';
import { startAudioCapture } from '../Calls/liveSubtitles';
import {
    initiateGroupCall, joinGroupCall, leaveGroupCall, declineGroupCall
} from '../../services/groupCallsApi';
import { getBalance as getMinuteBalance } from '../../services/minutePacksApi';
import './GroupCalls.css';

// Estados del state machine
const S_IDLE       = 'idle';
const S_INCOMING   = 'incoming';
const S_OUTGOING   = 'outgoing';
const S_CONNECTING = 'connecting';
const S_ACTIVE     = 'active';

// ── Tonos sintéticos (Web Audio API) ──────────────────────────────────
// Reutilizan el AudioContext global desbloqueado por CallManager (1:1).
// Patrón calcado para mantener consistencia auditiva entre ambos sistemas.

function playRingtone() {
    let ctx;
    try {
        ctx = window.__kaixoAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch { return () => {}; }
    let stopped = false;
    let timerId = null;
    const beep = (freq, durationMs, vol = 0.45) => {
        if (stopped) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(vol, now + 0.05);
        gain.gain.setValueAtTime(vol, now + durationMs / 1000 - 0.05);
        gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + durationMs / 1000);
    };
    const cycle = () => {
        if (stopped) return;
        beep(480, 400);
        setTimeout(() => beep(620, 400), 500);
        timerId = setTimeout(cycle, 2000);
    };
    cycle();
    return () => {
        stopped = true;
        if (timerId) clearTimeout(timerId);
        if (ctx !== window.__kaixoAudioCtx) { try { ctx.close(); } catch {} }
    };
}

function playRingback() {
    let ctx;
    try {
        ctx = window.__kaixoAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch { return () => {}; }
    let stopped = false;
    let timerId = null;
    const tone = (freq, durationMs, vol = 0.30) => {
        if (stopped) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(vol, now + 0.04);
        gain.gain.setValueAtTime(vol, now + durationMs / 1000 - 0.06);
        gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + durationMs / 1000);
    };
    const cycle = () => {
        if (stopped) return;
        tone(440, 400);
        setTimeout(() => tone(440, 400), 500);
        timerId = setTimeout(cycle, 3000);
    };
    cycle();
    return () => {
        stopped = true;
        if (timerId) clearTimeout(timerId);
        if (ctx !== window.__kaixoAudioCtx) { try { ctx.close(); } catch {} }
    };
}

function startVibrate() {
    if (!navigator.vibrate) return () => {};
    let stopped = false;
    const cycle = () => {
        if (stopped) return;
        try { navigator.vibrate([400, 200, 400]); } catch {}
        setTimeout(cycle, 2000);
    };
    cycle();
    return () => { stopped = true; try { navigator.vibrate(0); } catch {} };
}

export default function GroupCallManager({ currentUser }) {
    const [state, setState]         = useState(S_IDLE);
    const [callInfo, setCallInfo]   = useState(null);
    // participants: { id, name, avatar, language, isLocal, hasAudio, isMuted, audioLevel }
    const [participants, setParticipants] = useState([]);
    const [muted, setMuted]         = useState(false);
    const [error, setError]         = useState('');
    // Subtítulos traducidos: hasta 3 líneas. Cada una {id, text, pending, speakerName, ts}
    const [subtitleLines, setSubtitleLines] = useState([]);
    // Modo Conferencia: solo se muestra el speaker activo a tamaño grande,
    // los demás aparecen como tiles pequeñas. Útil para grupos grandes.
    const [conferenceMode, setConferenceMode] = useState(false);
    const [activeSpeakerId, setActiveSpeakerId] = useState(null);
    // Map identity → MediaStreamTrack de vídeo subscrito (incluye local si publica)
    const [videoTracks, setVideoTracks] = useState(new Map());
    // Saldo de minutos premium del usuario (para mostrar en el banner)
    const [minutesBalance, setMinutesBalance] = useState(null);

    const roomRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const localVideoTrackRef = useRef(null);
    const audioCaptureRef = useRef(null);     // STT capture handle (stop())
    const subtitleTimersRef = useRef(new Map()); // id → setTimeout
    // Espejo síncrono de subtitleLines para dedup sin esperar el re-render.
    const subtitleLinesRef = useRef([]);
    // useEffect mantiene el ref alineado con el state. Permite leer
    // el valor síncrono dentro del listener DataReceived sin batch races.
    useEffect(() => { subtitleLinesRef.current = subtitleLines; }, [subtitleLines]);
    // Stops para ringtone (receptor), ringback (caller) y vibración.
    const stopRingtoneRef = useRef(null);
    const stopRingbackRef = useRef(null);
    const stopVibrateRef  = useRef(null);

    // ── Reset / cleanup ─────────────────────────────────────────────
    const resetToIdle = useCallback(() => {
        try { stopRingtoneRef.current?.(); } catch {}
        try { stopRingbackRef.current?.(); } catch {}
        try { stopVibrateRef.current?.(); } catch {}
        stopRingtoneRef.current = null;
        stopRingbackRef.current = null;
        stopVibrateRef.current = null;
        try { audioCaptureRef.current?.stop?.(); } catch {}
        try {
            const sock = getSocket();
            sock?.emit?.('call:subtitles:stop');
        } catch {}
        try { localAudioTrackRef.current?.stop(); } catch {}
        try { localVideoTrackRef.current?.stop(); } catch {}
        try { roomRef.current?.disconnect(); } catch {}
        // Limpiar timers de subtítulos
        try { subtitleTimersRef.current.forEach(t => clearTimeout(t)); } catch {}
        subtitleTimersRef.current = new Map();
        audioCaptureRef.current = null;
        localAudioTrackRef.current = null;
        localVideoTrackRef.current = null;
        roomRef.current = null;
        setCallInfo(null);
        setParticipants([]);
        setMuted(false);
        setError('');
        setSubtitleLines([]);
        setConferenceMode(false);
        setActiveSpeakerId(null);
        setVideoTracks(new Map());
        setState(S_IDLE);
    }, []);

    // ── Conectar a la sala LiveKit ──────────────────────────────────
    const connectToRoom = useCallback(async ({ url, token, type, members, currentLocalId, callId }) => {
        if (!url || !token) {
            setError('Configuración LiveKit incompleta');
            resetToIdle();
            return;
        }
        try {
            const room = new Room({
                adaptiveStream: true,
                dynacast: true
            });
            roomRef.current = room;
            // Stash del callId en el room para que el bloque STT lo lea al
            // enviar 'call:subtitles:start'.
            if (callId) roomRef.current._kaixoCallId = callId;

            const updateParticipantsFromRoom = () => {
                const list = [];
                // local
                const lp = room.localParticipant;
                list.push({
                    id: lp.identity,
                    name: lp.name || (members?.find(m => m.id === lp.identity)?.name) || 'Tú',
                    avatar: members?.find(m => m.id === lp.identity)?.avatar || '',
                    isLocal: true,
                    isMuted: lp.isMicrophoneEnabled === false
                });
                // remotos
                room.remoteParticipants.forEach(p => {
                    list.push({
                        id: p.identity,
                        name: p.name || (members?.find(m => m.id === p.identity)?.name) || p.identity,
                        avatar: members?.find(m => m.id === p.identity)?.avatar || '',
                        isLocal: false,
                        isMuted: !p.audioTrackPublications.size || [...p.audioTrackPublications.values()].every(pub => pub.isMuted)
                    });
                });
                setParticipants(list);
            };

            room
                .on(RoomEvent.ParticipantConnected, (p) => {
                    // Alguien entró → si soy el caller, parar ring-back.
                    try { stopRingbackRef.current?.(); } catch {}
                    stopRingbackRef.current = null;
                    updateParticipantsFromRoom();
                })
                .on(RoomEvent.ParticipantDisconnected, (p) => {
                    setVideoTracks(prev => {
                        const next = new Map(prev);
                        next.delete(p.identity);
                        return next;
                    });
                    updateParticipantsFromRoom();
                })
                .on(RoomEvent.TrackMuted, updateParticipantsFromRoom)
                .on(RoomEvent.TrackUnmuted, updateParticipantsFromRoom)
                .on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
                    if (track.kind === Track.Kind.Audio) {
                        const el = track.attach();
                        el.style.display = 'none';
                        document.body.appendChild(el);
                        track.once('ended', () => { try { el.remove(); } catch {} });
                    } else if (track.kind === Track.Kind.Video) {
                        // Guardar el track para que un <video ref> en el render
                        // pueda hacer track.attach(el).
                        setVideoTracks(prev => {
                            const next = new Map(prev);
                            next.set(participant.identity, track);
                            return next;
                        });
                    }
                })
                .on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
                    if (track.kind === Track.Kind.Video) {
                        setVideoTracks(prev => {
                            const next = new Map(prev);
                            next.delete(participant.identity);
                            return next;
                        });
                    }
                })
                // ── Active speaker — para Modo Conferencia
                .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                    if (speakers && speakers.length > 0) {
                        setActiveSpeakerId(speakers[0].identity);
                    }
                })
                // ── Subtítulos: el backend hace broadcast por DataChannel
                //    con topic 'kaixo-subtitle' a los identities con cada
                //    idioma. Nosotros recibimos solo las nuestras (en el
                //    idioma del user actual).
                .on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
                    if (topic && topic !== 'kaixo-subtitle') return;
                    try {
                        const text = new TextDecoder().decode(payload);
                        const data = JSON.parse(text);
                        const MAX_LINES = 3;
                        const PENDING_TTL_MS = 6000;
                        const FINAL_TTL_MS = 9000;

                        if (data?.kind === 'subtitle_pending' && data.id) {
                            const line = {
                                id: data.id,
                                text: null,
                                pending: true,
                                speakerName: data.speakerName,
                                ts: Date.now()
                            };
                            setSubtitleLines(prev => {
                                if (prev.some(l => l.id === data.id)) return prev;
                                return [...prev, line].slice(-MAX_LINES);
                            });
                            const t = setTimeout(() => {
                                setSubtitleLines(prev => prev.filter(l => !(l.id === data.id && l.pending)));
                                subtitleTimersRef.current.delete(data.id);
                            }, PENDING_TTL_MS);
                            subtitleTimersRef.current.set(data.id, t);
                            return;
                        }

                        if (data?.kind === 'subtitle' && data.text) {
                            // Dedup contra texto reciente. Si el AEC del navegador
                            // falla y el mic del oyente capta el audio del altavoz,
                            // se transcribe DOS veces (una por speaker, otra por
                            // oyente eco). Descartamos el segundo si es idéntico
                            // a una línea final recibida en los últimos 6 segundos.
                            const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
                            const incoming = norm(data.text);
                            const now = Date.now();
                            const isDupe = subtitleLinesRef.current.some(l =>
                                !l.pending &&
                                norm(l.text) === incoming &&
                                (now - (l.ts || 0)) < 6000
                            );
                            if (isDupe) {
                                if (data.replacesId) {
                                    setSubtitleLines(prev => prev.filter(l => l.id !== data.replacesId));
                                    const old = subtitleTimersRef.current.get(data.replacesId);
                                    if (old) { clearTimeout(old); subtitleTimersRef.current.delete(data.replacesId); }
                                }
                                return;
                            }
                            const replacesId = data.replacesId;
                            if (replacesId) {
                                const old = subtitleTimersRef.current.get(replacesId);
                                if (old) { clearTimeout(old); subtitleTimersRef.current.delete(replacesId); }
                                let replaced = false;
                                setSubtitleLines(prev => {
                                    const next = prev.map(l => {
                                        if (l.id === replacesId) {
                                            replaced = true;
                                            return { ...l, text: data.text, pending: false, ts: Date.now() };
                                        }
                                        return l;
                                    });
                                    return next;
                                });
                                if (replaced) {
                                    const t = setTimeout(() => {
                                        setSubtitleLines(prev => prev.filter(l => l.id !== replacesId));
                                    }, FINAL_TTL_MS);
                                    subtitleTimersRef.current.set(replacesId, t);
                                    return;
                                }
                            }
                            const newId = data.id || `s-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
                            setSubtitleLines(prev => {
                                return [...prev, {
                                    id: newId,
                                    text: data.text,
                                    pending: false,
                                    speakerName: data.speakerName,
                                    ts: Date.now()
                                }].slice(-MAX_LINES);
                            });
                            const t = setTimeout(() => {
                                setSubtitleLines(prev => prev.filter(l => l.id !== newId));
                            }, FINAL_TTL_MS);
                            subtitleTimersRef.current.set(newId, t);
                        }
                    } catch (err) {
                        console.warn('[groupCall] DataReceived parse error:', err.message);
                    }
                })
                .on(RoomEvent.Disconnected, (reason) => {
                    const reasonName = ({
                        0: 'UNKNOWN', 1: 'CLIENT_INITIATED', 2: 'DUPLICATE_IDENTITY',
                        3: 'SERVER_SHUTDOWN', 4: 'PARTICIPANT_REMOVED', 5: 'ROOM_DELETED',
                        6: 'STATE_MISMATCH', 7: 'JOIN_FAILURE', 8: 'MIGRATION'
                    })[reason] || `code=${reason}`;
                    console.warn('[groupCall] Room Disconnected. reason=', reason, '(', reasonName, ') identity=', room.localParticipant?.identity);
                    setError(`LiveKit cerró la sala: ${reasonName}`);
                    // Dejar 5s para que el usuario vea el mensaje antes de cerrar el modal
                    setTimeout(() => resetToIdle(), 5000);
                });

            console.log('[groupCall] connecting to room', { url, identity: currentLocalId, callId });
            await room.connect(url, token);
            console.log('[groupCall] connected. localId=', room.localParticipant?.identity, 'remotes=', room.remoteParticipants.size);

            // publicar audio local
            const audio = await createLocalAudioTrack({ noiseSuppression: true, echoCancellation: true });
            localAudioTrackRef.current = audio;
            await room.localParticipant.publishTrack(audio);

            // publicar vídeo si la llamada lo pidió. Guardamos el track local
            // en videoTracks para que el render lo muestre en su tile.
            if (type === 'video') {
                try {
                    const video = await createLocalVideoTrack({ resolution: { width: 640, height: 480 } });
                    localVideoTrackRef.current = video;
                    await room.localParticipant.publishTrack(video);
                    setVideoTracks(prev => {
                        const next = new Map(prev);
                        next.set(room.localParticipant.identity, video);
                        return next;
                    });
                } catch (e) {
                    console.warn('[groupCall] video local no publicado:', e?.message);
                }
            }

            // ── Subtítulos: arrancar sesión de transcripción del audio local
            //    enviándolo al backend por socket. El backend transcribe con
            //    Deepgram, traduce a los idiomas de los demás participantes y
            //    devuelve por DataChannel a cada identity. Reutilizamos el
            //    pipeline de 1:1 — admite calls grupales sin cambios.
            try {
                const sock = getSocket();
                const sttCallId = roomRef.current?._kaixoCallId || callId;
                if (sock && audio.mediaStream && sttCallId) {
                    sock.emit(
                        'call:subtitles:start',
                        { callId: sttCallId },
                        async (ack) => {
                            if (!ack?.ok) {
                                console.warn('[groupCall] subtitles no iniciados:', ack?.reason);
                                return;
                            }
                            try {
                                audioCaptureRef.current = await startAudioCapture({
                                    stream: audio.mediaStream,
                                    onChunk: (buf) => {
                                        if (!sock.connected) return;
                                        try { sock.emit('call:subtitles:audio', buf); } catch {}
                                    }
                                });
                            } catch (e) {
                                console.warn('[groupCall] startAudioCapture falló:', e?.message);
                            }
                        }
                    );
                }
            } catch (e) {
                console.warn('[groupCall] STT bootstrap falló:', e?.message);
            }

            updateParticipantsFromRoom();
            setState(S_ACTIVE);
        } catch (err) {
            console.error('[groupCall] connect error:', err);
            setError(err?.message || 'No se pudo conectar');
            resetToIdle();
        }
    }, [resetToIdle]);

    // ── Iniciar (outgoing) ──────────────────────────────────────────
    const startCall = useCallback(async ({ groupId, groupName, groupAvatar, type = 'voice' }) => {
        if (state !== S_IDLE) return;
        setError('');
        setState(S_OUTGOING);
        setCallInfo({ groupId, groupName, groupAvatar, type, isInitiator: true });
        // Tono de ring-back para el caller mientras espera que alguien
        // del grupo acepte. Para automáticamente cuando entra el primer
        // remoto (en updateParticipantsFromRoom).
        try { stopRingbackRef.current = playRingback(); } catch {}

        try {
            const { data } = await initiateGroupCall({ groupId, type });
            const d = data?.data;
            if (!d?.callId) throw new Error('Respuesta inválida');
            setCallInfo(prev => ({ ...prev, callId: d.callId, members: d.members, roomName: d.roomName }));
            setState(S_CONNECTING);
            await connectToRoom({
                url: d.url,
                token: d.token,
                type,
                members: d.members,
                currentLocalId: currentUser?._id,
                callId: d.callId
            });
        } catch (err) {
            const code = err?.response?.data?.code;
            const msg = err?.response?.data?.error || err.message || 'Error iniciando llamada';
            setError(msg);
            // Mostrar el error 2s antes de cerrar para que se vea
            setTimeout(() => resetToIdle(), 2500);
            console.warn('[groupCall] start error:', code, msg);
        }
    }, [state, currentUser, connectToRoom, resetToIdle]);

    // ── Aceptar (incoming) ──────────────────────────────────────────
    const acceptCall = useCallback(async () => {
        console.log('[groupCall] acceptCall pulsado. state=', state, 'callId=', callInfo?.callId);
        if (state !== S_INCOMING || !callInfo?.callId) {
            console.warn('[groupCall] acceptCall ignorado — state o callId inválidos');
            return;
        }
        // Parar ring/vibración inmediatamente para feedback al usuario.
        try { stopRingtoneRef.current?.(); } catch {}
        try { stopVibrateRef.current?.(); } catch {}
        stopRingtoneRef.current = null;
        stopVibrateRef.current = null;
        setState(S_CONNECTING);
        try {
            console.log('[groupCall] llamando joinGroupCall HTTP...');
            const { data } = await joinGroupCall(callInfo.callId);
            console.log('[groupCall] joinGroupCall respondió:', data);
            const d = data?.data;
            if (!d?.url || !d?.token) {
                throw new Error('Respuesta inválida del backend (sin url/token)');
            }
            await connectToRoom({
                url: d.url,
                token: d.token,
                type: callInfo.type,
                members: callInfo.members || [],
                currentLocalId: currentUser?._id,
                callId: callInfo.callId
            });
        } catch (err) {
            console.error('[groupCall] error en acceptCall:', err?.response?.status, err?.response?.data, err?.message);
            setError(err?.response?.data?.error || err.message || 'No se pudo aceptar');
            setTimeout(() => resetToIdle(), 4000);
        }
    }, [state, callInfo, currentUser, connectToRoom, resetToIdle]);

    // ── Rechazar (incoming) ─────────────────────────────────────────
    const declineCall = useCallback(async () => {
        if (!callInfo?.callId) { resetToIdle(); return; }
        try { await declineGroupCall(callInfo.callId); } catch {}
        resetToIdle();
    }, [callInfo, resetToIdle]);

    // ── Colgar (active) ─────────────────────────────────────────────
    const hangUp = useCallback(async () => {
        if (callInfo?.callId) {
            try { await leaveGroupCall(callInfo.callId); } catch {}
        }
        resetToIdle();
    }, [callInfo, resetToIdle]);

    // ── Mute toggle ─────────────────────────────────────────────────
    const toggleMute = useCallback(async () => {
        if (!roomRef.current) return;
        const lp = roomRef.current.localParticipant;
        const next = !muted;
        try {
            await lp.setMicrophoneEnabled(!next);
            setMuted(next);
        } catch {}
    }, [muted]);

    // ── Listener de window (independiente del socket) ───────────────
    // Importante: registrar SIEMPRE, sin condicionarlo al socket. Cuando
    // GroupCallManager monta, el socket puede no estar listo todavía
    // (QR vinculación tarda) — pero el botón "Llamar" del chat ya despacha
    // el evento. Si el listener no estuviera registrado, se perdería el
    // primer click. Aquí lo enganchamos en un useEffect aparte que NO
    // depende del socket.
    useEffect(() => {
        const onWindowStart = (e) => {
            const d = e?.detail || {};
            if (!d.groupId) return;
            startCall({
                groupId: d.groupId,
                groupName: d.groupName,
                groupAvatar: d.groupAvatar,
                type: d.type || 'voice'
            });
        };
        window.addEventListener('kaixo:group_call_start', onWindowStart);
        return () => window.removeEventListener('kaixo:group_call_start', onWindowStart);
    }, [startCall]);

    // ── Listeners de socket — esperan a que getSocket() devuelva algo
    useEffect(() => {
        let socket = getSocket();
        let pollTimer = null;

        const onIncoming = (data) => {
            if (state !== S_IDLE) return;
            setCallInfo({
                callId: data.callId,
                groupId: data.groupId,
                groupName: data.groupName,
                groupAvatar: data.groupAvatar,
                type: data.type,
                from: data.from,
                isInitiator: false,
                members: data.members || []
            });
            setState(S_INCOMING);
            // Ring tone + vibración hasta que el user acepte/rechace o
            // hasta que el caller cuelgue. resetToIdle los para.
            try { stopRingtoneRef.current = playRingtone(); } catch {}
            try { stopVibrateRef.current = startVibrate(); } catch {}
        };
        const onAccepted = () => {};
        const onUserLeft = () => {};
        const onCallEnded = (data) => {
            if (callInfo?.callId === data.callId) resetToIdle();
        };
        const onMinutesConsumed = (data) => {
            if (typeof data?.remaining === 'number') setMinutesBalance(data.remaining);
        };

        const attach = (s) => {
            s.on('incoming_group_call', onIncoming);
            s.on('group_call_accepted', onAccepted);
            s.on('group_call_user_left', onUserLeft);
            s.on('group_call_ended', onCallEnded);
            s.on('minute_pack_consumed', onMinutesConsumed);
        };

        if (socket) {
            attach(socket);
        } else {
            // Polling barato: cada 800ms reintenta hasta que getSocket() devuelve algo.
            // Soluciona el caso en que GroupCallManager monta antes que el socket.
            pollTimer = setInterval(() => {
                socket = getSocket();
                if (socket) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                    attach(socket);
                }
            }, 800);
        }

        return () => {
            if (pollTimer) clearInterval(pollTimer);
            if (socket) {
                socket.off('incoming_group_call', onIncoming);
                socket.off('group_call_accepted', onAccepted);
                socket.off('group_call_user_left', onUserLeft);
                socket.off('group_call_ended', onCallEnded);
                socket.off('minute_pack_consumed', onMinutesConsumed);
            }
        };
    }, [state, callInfo, resetToIdle]);

    // Cargar saldo de minutos cuando hay user. Se refresca al montar,
    // al iniciar/aceptar una llamada y cuando llega minute_pack_consumed.
    useEffect(() => {
        if (!currentUser?._id) return;
        if (state === S_IDLE) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await getMinuteBalance();
                if (!cancelled && typeof data?.totalMinutes === 'number') {
                    setMinutesBalance(data.totalMinutes);
                }
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [currentUser?._id, state]);

    // ── Render ──────────────────────────────────────────────────────
    if (state === S_IDLE) return null;

    return ReactDOM.createPortal(
        <div className="gc-overlay">
            <div className="gc-modal">
                <div className="gc-header">
                    <div className="gc-group-info">
                        {callInfo?.groupAvatar
                            ? <img className="gc-group-avatar" src={callInfo.groupAvatar} alt="" />
                            : <div className="gc-group-avatar gc-group-avatar-fallback">👥</div>}
                        <div>
                            <div className="gc-group-name">{callInfo?.groupName || 'Grupo'}</div>
                            <div className="gc-state-label">
                                {state === S_INCOMING && (callInfo?.type === 'video' ? 'Videollamada entrante' : 'Llamada entrante')}
                                {state === S_OUTGOING && 'Llamando…'}
                                {state === S_CONNECTING && 'Conectando…'}
                                {state === S_ACTIVE && 'En llamada'}
                            </div>
                        </div>
                    </div>
                    {state === S_INCOMING && callInfo?.from?.name && (
                        <div className="gc-from">{callInfo.from.name} llama al grupo</div>
                    )}
                </div>

                {/* Banner de compra de minutos (placeholder fase B). El cartel
                    se traduce al idioma del usuario en `currentUser.language`. */}
                <BuyMinutesHint
                    language={currentUser?.language}
                    visible={state === S_OUTGOING || state === S_ACTIVE}
                    balanceMinutes={minutesBalance}
                />

                {error && <div className="gc-error">{error}</div>}

                {state === S_ACTIVE && participants.length <= 1 && (
                    <div className="gc-waiting">
                        <div className="gc-waiting-pulse">⏳</div>
                        <p>Eres el único en la llamada</p>
                        <small>Esperando a que alguien del grupo se una. Los subtítulos traducidos aparecerán cuando hablen otros participantes en otros idiomas.</small>
                    </div>
                )}

                {state === S_ACTIVE && (() => {
                    // Modo Conferencia: el speaker activo grande arriba +
                    // tiles pequeñas abajo. Sin Conferencia: rejilla normal.
                    const speakerInActive = conferenceMode && activeSpeakerId
                        ? participants.find(p => p.id === activeSpeakerId)
                        : null;
                    const others = conferenceMode && speakerInActive
                        ? participants.filter(p => p.id !== speakerInActive.id)
                        : participants;
                    return (
                        <>
                            {speakerInActive && (
                                <div className="gc-spotlight">
                                    <ParticipantTile
                                        p={speakerInActive}
                                        videoTrack={videoTracks.get(speakerInActive.id)}
                                        large
                                    />
                                </div>
                            )}
                            <div className={`gc-participants ${conferenceMode ? 'gc-participants-strip' : ''}`}>
                                {others.map(p => (
                                    <ParticipantTile
                                        key={p.id}
                                        p={p}
                                        videoTrack={videoTracks.get(p.id)}
                                    />
                                ))}
                            </div>
                            {/* Subtítulos traducidos: 3 líneas máx en banda inferior. */}
                            {subtitleLines.length > 0 && (
                                <div className="gc-subtitles">
                                    {subtitleLines.map(line => (
                                        <div key={line.id} className={`gc-sub-line ${line.pending ? 'gc-sub-pending' : ''}`}>
                                            {line.speakerName && (
                                                <span className="gc-sub-speaker">{line.speakerName}: </span>
                                            )}
                                            <span className="gc-sub-text">
                                                {line.pending ? <em>traduciendo…</em> : line.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    );
                })()}

                <div className="gc-actions">
                    {state === S_INCOMING && (
                        <>
                            <button className="gc-btn gc-btn-accept" onClick={acceptCall} aria-label="Aceptar">
                                <Icons.Phone size={26} />
                            </button>
                            <button className="gc-btn gc-btn-decline" onClick={declineCall} aria-label="Rechazar">
                                <Icons.Phone size={26} />
                            </button>
                        </>
                    )}
                    {(state === S_OUTGOING || state === S_CONNECTING || state === S_ACTIVE) && (
                        <>
                            {state === S_ACTIVE && (
                                <button
                                    className={`gc-btn ${muted ? 'gc-btn-muted' : 'gc-btn-mute'}`}
                                    onClick={toggleMute}
                                    aria-label={muted ? 'Desmutear' : 'Mutear'}
                                >
                                    {muted ? '🎙️🔇' : '🎙️'}
                                </button>
                            )}
                            {state === S_ACTIVE && (
                                <button
                                    className={`gc-btn ${conferenceMode ? 'gc-btn-conf-on' : 'gc-btn-conf'}`}
                                    onClick={() => setConferenceMode(v => !v)}
                                    aria-label="Modo Conferencia"
                                    title="Modo Conferencia: solo el ponente activo"
                                >
                                    🎤
                                </button>
                            )}
                            <button className="gc-btn gc-btn-decline" onClick={hangUp} aria-label="Colgar">
                                <Icons.Phone size={26} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

// ── ParticipantTile: avatar + opcional <video> attach ────────────────
function ParticipantTile({ p, videoTrack, large = false }) {
    const videoRef = useRef(null);
    useEffect(() => {
        const el = videoRef.current;
        if (!el || !videoTrack) return;
        try { videoTrack.attach(el); } catch {}
        return () => {
            try { videoTrack.detach(el); } catch {}
        };
    }, [videoTrack]);

    const showVideo = !!videoTrack;

    return (
        <div className={`gc-participant ${p.isLocal ? 'is-local' : ''} ${large ? 'gc-participant-large' : ''}`}>
            <div className="gc-avatar">
                {showVideo ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted={p.isLocal}
                        controls={false}
                        disablePictureInPicture
                        className="gc-video"
                    />
                ) : p.avatar ? (
                    <img src={p.avatar} alt="" />
                ) : (
                    <span>{(p.name || '?').charAt(0).toUpperCase()}</span>
                )}
                {p.isMuted && <span className="gc-mute-badge">🎙️🔇</span>}
            </div>
            <div className="gc-participant-name">{p.isLocal ? 'Tú' : p.name}</div>
        </div>
    );
}

// ── Cartel "Compra minutos" traducido a los idiomas de KAIXO ──────────
function BuyMinutesHint({ language, visible, balanceMinutes }) {
    if (!visible) return null;
    const txt = MINUTES_HINT[language] || MINUTES_HINT.español;
    const balLabel = MINUTES_BALANCE_LABEL[language] || MINUTES_BALANCE_LABEL.español;
    return (
        <a className="gc-buy-hint" href="https://kaixo.ai/minutos/" target="_blank" rel="noopener noreferrer">
            {typeof balanceMinutes === 'number' && balanceMinutes > 0 ? (
                <>⏱️ {balLabel} <strong>{balanceMinutes}</strong> · {txt}</>
            ) : (
                <>💳 {txt}</>
            )}
        </a>
    );
}

const MINUTES_BALANCE_LABEL = {
    español: 'Saldo:',
    inglés: 'Balance:',
    francés: 'Solde:',
    alemán: 'Guthaben:',
    portugués: 'Saldo:',
    italiano: 'Saldo:',
    euskera: 'Saldoa:',
    chino: '余额:',
    japonés: '残高:',
    árabe: 'الرصيد:',
    hindi: 'शेष:',
    ruso: 'Баланс:',
    coreano: '잔액:',
    turco: 'Bakiye:',
    indonesio: 'Saldo:'
};

const MINUTES_HINT = {
    español: 'Puedes comprar minutos de videollamada con traducción simultánea',
    inglés:  'You can buy video call minutes with live translation',
    francés: 'Achetez des minutes de visioconférence avec traduction en direct',
    alemán:  'Kaufen Sie Videoanruf-Minuten mit Live-Übersetzung',
    portugués: 'Compre minutos de videochamada com tradução em tempo real',
    italiano:  'Acquista minuti di videochiamata con traduzione in tempo reale',
    euskera:   'Erosi bideo-deietako minutuak zuzeneko itzulpenarekin',
    chino:     '购买带实时翻译的视频通话分钟',
    japonés:   'リアルタイム翻訳付きビデオ通話の分数を購入',
    árabe:     'اشترِ دقائق مكالمات فيديو مع ترجمة فورية',
    hindi:     'लाइव अनुवाद के साथ वीडियो कॉल के मिनट खरीदें',
    ruso:      'Купите минуты видеозвонков с переводом в реальном времени',
    coreano:   '실시간 번역 화상 통화 분 구매',
    turco:     'Canlı çeviri ile görüntülü arama dakikası satın alın',
    indonesio: 'Beli menit panggilan video dengan terjemahan langsung'
};
